import React, { useState } from 'react';
import moment from 'moment-timezone';
import {
  Modal,
  ModalSection,
  GridColumn,
  Grid,
  GridRow,
  Paragraph,
  Gutter,
  CalendarTimeZoneUtil,
  TimezoneDropdown,
  Form,
  Calendar,
  CalendarPermissionsUtil,
  Layout,
  LayoutItem,
  Checkbox,
  DateUtil,
  CalendarEventsUtil,
  Banner,
  EVENT_STATES,
  CalendarLegendKey,
  ParagraphHeader,
} from '@axiom/ui';
import { PermissionImpersonationRoles, NO_TIMEZONE } from '@axiom/const';
import type {
  Calendar as CalendarApiType,
  User,
  CalendarEventType,
  Contact,
  AfcSubmission,
  Account,
} from '@axiom/validation';

import type { InterviewFlowCandidate } from '../../types/interview-candidate.types';
import { CalendarApi } from '../../api/calendar-api';
import { CandidateApi } from '../../api/candidate-api';
import { TalentInterviewConfirmModal } from '../TalentInterviewConfirmModal/TalentInterviewConfirmModal';
import { TalentInterviewPromptModal } from '../TalentInterviewPromptModal/TalentInterviewPromptModal';
import type { TalentInterviewConfirmModalType } from '../../schema/talent-interview-confirm-modal-schema';
import { TalentInterviewLegalTeamQuestionsModal } from '../TalentInterviewLegalTeamQuestionsModal/TalentInterviewLegalTeamQuestionsModal';
import { TalentInterviewModalFooter } from '../TalentInterviewModalFooter/TalentInterviewModalFooter';
import { TalentInterviewModalHeader } from '../TalentInterviewModalHeader/TalentInterviewModalHeader';
import type { TalentInterviewLegalTeamQuestionsType } from '../../schema/talent-interview-legal-team-questions-modal-schema';
import { TalentInterviewScheduleModalSchema } from '../../schema/talent-interview-schedule-modal-schema';

export interface InterviewScheduleModalType {
  accountId?: Account['id'];
  submission?: AfcSubmission;
  candidate: InterviewFlowCandidate;
  onClose: () => void;
  onConfirm: (
    arg: TalentInterviewConfirmModalType,
    scheduledEvent?: CalendarEventType,
    legalTeamData?: Partial<TalentInterviewLegalTeamQuestionsType>
  ) => Promise<unknown>;
  onNoTimeAvailableFlowConfirm: (
    arg: TalentInterviewConfirmModalType,
    legalTeamData?: Partial<TalentInterviewLegalTeamQuestionsType>
  ) => Promise<unknown>;
  calendarData: CalendarApiType;
  user: User;
  primaryContactId?: Contact['id'];
  tempEvent?: CalendarEventType;
}

export const TalentInterviewScheduleFlow = ({
  accountId,
  submission,
  candidate,
  calendarData,
  onClose,
  onConfirm,
  user,
  primaryContactId,
  onNoTimeAvailableFlowConfirm,
  tempEvent,
}: InterviewScheduleModalType) => {
  const dayWeekMode = {
    slots: 4,
    increments: 15,
  };
  const { userPermissions } = CalendarPermissionsUtil(
    user,
    PermissionImpersonationRoles.client
  );

  const calendarPermissions = userPermissions();
  const talentIana = candidate.calendar?.timezone;
  const guessIana = moment.tz.guess();
  const timezone = CalendarTimeZoneUtil.getZoneFromMomentZone(
    moment.tz(new Date(), talentIana || guessIana)
  );
  const [showScheduleModal, setShowScheduleModal] = useState(true);
  const [confirmModalEvents, setConfirmModalEvents] =
    useState<CalendarEventType[]>();
  const [showConfirmModalWithEvents, setShowConfirmModalWithEvents] =
    useState(false);
  const [
    showConfirmModalWithFormattedPrompt,
    setShowConfirmModalWithFormattedPrompt,
  ] = useState(false);
  const [showInterviewPromptModal, setShowInterviewPromptModal] =
    useState(false);
  const [formattedPrompt, setFormattedPrompt] = useState('');
  const [
    showInterviewLegalTeamQuestionsModal,
    setShowInterviewLegalTeamQuestionsModal,
  ] = useState(false);
  const [legalTeamData, setLegalTeamData] =
    useState<Partial<TalentInterviewLegalTeamQuestionsType>>();

  const [calculatedTimezone, setCalculatedTimezone] = useState(timezone);

  const calendarEvents = (calendarData.events || []).reduce<{
    events: CalendarEventType[];
    bgEvents: CalendarEventType[];
  }>(
    (crnt, item) => {
      item.state = item.state ?? EVENT_STATES.SAVED;

      if (!item.busy) {
        if (
          !CalendarEventsUtil.isInThePast(item.endTime, calendarPermissions) &&
          moment(item.startTime).isBefore(moment().add(90, 'days'), 'day')
        ) {
          // free time that is between NOW and up to 90 in the future
          crnt.bgEvents.push(item);
        }
      } else if (
        !CalendarEventsUtil.isInThePast(item.endTime, calendarPermissions)
      ) {
        // interviews in the future
        crnt.events.push(item);
      }

      return crnt;
    },
    { events: [], bgEvents: [] }
  );

  const refreshData = async () => {
    await Promise.all([
      CalendarApi.refreshCalendar(calendarData.id),
      CalendarApi.refreshCalendarEvents(calendarData.id),
      CandidateApi.refreshCandidate(candidate.id),
    ]);
  };

  const createAndAddNewEvent = () => {
    const { events } = calendarEvents;
    if (tempEvent) {
      const newEvent = {
        id: `UNSAVED_${Math.round(Math.random() * 1e17) + 1e17}`,
        title: 'Interview',
        startTime: tempEvent.startTime,
        endTime: moment(tempEvent.startTime).add(30, 'minutes').toISOString(),
        name: 'EVENT',
        busy: true,
        error: null,
        state: EVENT_STATES.NEW,
      };
      events.push(newEvent);
    }

    return events;
  };

  return (
    <>
      {showScheduleModal ? (
        <Form
          name="INTERVIEW_AUTO_FORM"
          schema={TalentInterviewScheduleModalSchema}
          initialValues={{
            CALENDAR_COMPONENT: createAndAddNewEvent(),
            TIMEZONE_DROPDOWN:
              timezone === NO_TIMEZONE ? null : (timezone?.id ?? null),
            NO_AVAILABILITY: false,
            BACKGROUND_EVENTS: calendarEvents.bgEvents,
          }}
          onSubmit={(
            formData,
            {
              setFieldError,
            }: { setFieldError: (field: string, message: string) => void },
            values
          ) => {
            const { staticEvents } = CalendarEventsUtil.cleanAndOrganiseEvents({
              permissions: calendarPermissions,
              initData: [],
              changedEvents: values.CALENDAR_COMPONENT,
            });

            setCalculatedTimezone(
              CalendarTimeZoneUtil.getZoneFromId(
                formData.TIMEZONE_DROPDOWN ?? timezone?.id
              )
            );

            if (formData.NO_AVAILABILITY) {
              setShowInterviewPromptModal(true);
              setShowScheduleModal(false);
            } else if (staticEvents.length > 0) {
              setConfirmModalEvents(staticEvents);
              if (submission?.selfService) {
                setShowInterviewLegalTeamQuestionsModal(true);
              } else {
                setShowConfirmModalWithEvents(true);
              }
              setShowScheduleModal(false);
            }

            // no event changes aka calendar is required
            setFieldError(
              'CALENDAR_COMPONENT',
              'Please make at least one edit to continue.'
            );
          }}
        >
          {({ fireSubmit, values }) => {
            return (
              <Modal size="large" name="INTERVIEW_SCHEDULE_MODAL">
                <TalentInterviewModalHeader
                  candidate={candidate}
                  name="INTERVIEW_SCHEDULE_MODAL_HEADER"
                  onClose={onClose}
                />
                <ModalSection>
                  <Grid>
                    <GridRow>
                      <GridColumn widescreenWidth={4} smallScreenWidth={12}>
                        <ParagraphHeader name="SCHEDULE_GREETING">
                          Hello {user.firstName}. Please select an available
                          30-minute time slot from{' '}
                          {candidate.calculatedFirstName ||
                            candidate.calculatedDisplayName}
                          's interview calendar.
                        </ParagraphHeader>
                        <Gutter bottom="24px" />
                        <TimezoneDropdown name="TIMEZONE_DROPDOWN" />
                        <Gutter bottom="16px" />
                        <Banner>
                          <Paragraph>
                            If these times don't work, check this box and click
                            Next for more assistance.
                          </Paragraph>
                          <Gutter bottom="16px" />
                          <Checkbox
                            name="NO_AVAILABILITY"
                            displayValue="None of these times work for me."
                          />
                        </Banner>
                        <Gutter bottom="16px" />
                      </GridColumn>
                      <GridColumn widescreenWidth={8} smallScreenWidth={12}>
                        <Calendar
                          name="CALENDAR_COMPONENT"
                          permissions={calendarPermissions}
                          backgroundEvents={calendarEvents.bgEvents}
                          dayMode={dayWeekMode}
                          weekMode={dayWeekMode}
                          timezone={
                            values.TIMEZONE_DROPDOWN
                              ? CalendarTimeZoneUtil.getZoneFromId(
                                  values.TIMEZONE_DROPDOWN
                                ).iana
                              : timezone?.iana
                          }
                        />
                        <Gutter bottom="8px" />
                        <Layout position="left middle" horizontalGutter="8px">
                          <LayoutItem>
                            <CalendarLegendKey type="availability" />
                          </LayoutItem>
                          <LayoutItem rightGutter="16px">
                            <Paragraph name="LEGEND_TEXT">
                              {candidate.calculatedFirstName ||
                                candidate.calculatedDisplayName}
                              's availability
                            </Paragraph>
                          </LayoutItem>
                          <LayoutItem>
                            <CalendarLegendKey type="scheduled" />
                          </LayoutItem>
                          <LayoutItem rightGutter="16px">
                            <Paragraph name="LEGEND_TEXT_SCHEDULED">
                              Scheduled Interviews
                            </Paragraph>
                          </LayoutItem>
                          <LayoutItem>
                            <CalendarLegendKey type="new-interview" />
                          </LayoutItem>
                          <LayoutItem>
                            <Paragraph name="LEGEND_TEXT_NEW_INTERVIEW">
                              New interview
                            </Paragraph>
                          </LayoutItem>
                        </Layout>
                      </GridColumn>
                    </GridRow>
                  </Grid>
                </ModalSection>
                <TalentInterviewModalFooter
                  name="INTERVIEW_SCHEDULE_MODAL_FOOTER"
                  onClose={onClose}
                  onContinue={async () => {
                    await fireSubmit();
                  }}
                />
              </Modal>
            );
          }}
        </Form>
      ) : null}

      {showConfirmModalWithFormattedPrompt ? (
        <TalentInterviewConfirmModal
          loggedInUser={user}
          submission={submission}
          candidate={candidate}
          onClose={onClose}
          onConfirm={async confirmData => {
            await onNoTimeAvailableFlowConfirm(confirmData, legalTeamData);
          }}
          primaryContactId={primaryContactId}
          availabilityNote={formattedPrompt}
        />
      ) : null}

      {showConfirmModalWithEvents ? (
        <TalentInterviewConfirmModal
          onBackHandler={async () => {
            await refreshData();
            setShowScheduleModal(true);
          }}
          loggedInUser={user}
          submission={submission}
          candidate={candidate}
          onClose={onClose}
          onConfirm={async (confirmData, scheduledEvent) => {
            await onConfirm(confirmData, scheduledEvent, legalTeamData);
          }}
          scheduledEvent={confirmModalEvents?.[0]}
          interviewDate={`${DateUtil.displayBannerTimestamp(
            confirmModalEvents?.[0]?.startTime,
            confirmModalEvents?.[0]?.endTime,
            calculatedTimezone ?? timezone ?? NO_TIMEZONE
          )} ${calculatedTimezone?.abbr ?? ''} ${calculatedTimezone?.name ?? ''}`.trim()}
          primaryContactId={primaryContactId}
        />
      ) : null}

      {showInterviewPromptModal ? (
        <TalentInterviewPromptModal
          candidate={candidate}
          onClose={onClose}
          onContinue={(timezoneId, description) => {
            const t = CalendarTimeZoneUtil.getZoneFromId(timezoneId);
            setFormattedPrompt(`${t.abbr} - ${t.name}<br>${description}`);
            setShowInterviewPromptModal(false);

            if (submission?.selfService && accountId) {
              setShowInterviewLegalTeamQuestionsModal(true);
            } else {
              setShowConfirmModalWithFormattedPrompt(true);
            }
          }}
        />
      ) : null}

      {showInterviewLegalTeamQuestionsModal && accountId ? (
        <TalentInterviewLegalTeamQuestionsModal
          accountId={accountId}
          candidate={candidate}
          onClose={() => {
            setShowInterviewLegalTeamQuestionsModal(false);
            onClose();
          }}
          onContinue={data => {
            setLegalTeamData(data);
            setShowInterviewLegalTeamQuestionsModal(false);
            if (formattedPrompt) {
              setShowConfirmModalWithFormattedPrompt(true);
            } else {
              setShowConfirmModalWithEvents(true);
            }
          }}
          submissionId={submission?.submissionId}
        />
      ) : null}
    </>
  );
};
